import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiChevronRight, mdiClipboardPulseOutline, mdiLink, mdiViewDashboardOutline } from "@mdi/js";

import { useGetDashboard } from "../../config/swagger";
import { screens } from "../../config/screens";
import { getSaudeIcon, ordenarPorSaude } from "../../utils/saude";
import { formatarTempoChecagem } from "../../utils/tempo";
import { useToast } from "../../context/ToastContext";

import DashboardCard from "../../components/DashboardCard";
import ListCard from "../../components/ListCard";
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";

export default function Dashboard() {
	const navigate = useNavigate();
	const queryDashboard = useGetDashboard();
	const { user } = useAuth();
	const { handleToast } = useToast();

	const [linkModal, setLinkModal] = useState(false);
	const [tokenInput, setTokenInput] = useState("");

	function handleClick(item: string) {
		navigate(item);
	}

	function handleGerarLink() {
		if (tokenInput.trim()) {
			const link = `${window.location.origin}${process.env.PUBLIC_URL}/status?t=${encodeURIComponent(tokenInput.trim())}`;
			navigator.clipboard.writeText(link);
			handleToast("Link", "Link copiado com sucesso!", 5000);
			setLinkModal(false);
			setTokenInput("");
		} else {
			handleToast("Link", "Por favor, informe o token", 5000, "warning");
		}
	}

	function handleGerarLinkEmbed() {
		if (tokenInput.trim()) {
			const link = `${window.location.origin}${process.env.PUBLIC_URL}/statusEmbed?t=${encodeURIComponent(tokenInput.trim())}`;
			navigator.clipboard.writeText(link);
			handleToast("Link", "Link copiado com sucesso!", 5000);
			setLinkModal(false);
			setTokenInput("");
		} else {
			handleToast("Link", "Por favor, informe o token", 5000, "warning");
		}
	}

	return (
		<Layout>
			<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
				<Icon path={mdiViewDashboardOutline} size={1} className="me-2" /> Dashboard
			</h5>

			<Row className="mb-4">
				{screens
					.filter((screen) => screen.showDashboard)
					.filter((screen) => !screen.superUser || (screen.superUser && user?.superUser))
					.map((screen, screenKey) => {
						return (
							<Col lg={6} key={screenKey}>
								<ListCard
									onClick={() => {
										handleClick(screen.route);
									}}
								>
									<ListCard.Icon>
										<Icon path={screen.icon} size={1} className="text-muted me-3" />
									</ListCard.Icon>
									<ListCard.Content>
										<div className="flex-fill me-2">
											<div className="text-muted fw-lighter">{screen.name}</div>
											<div className="fw-light">{screen.description}</div>
										</div>
									</ListCard.Content>
									<ListCard.Action>
										<Icon path={mdiChevronRight} size={1} className="text-muted m-2" />
									</ListCard.Action>
								</ListCard>
							</Col>
						);
					})}
			</Row>

			<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
				<Icon path={mdiClipboardPulseOutline} size={1} className="me-2" /> Monitores por Grupo
				<div className="float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
					<Button
						variant="outline-dark"
						onClick={() => {
							setLinkModal(true);
							setTokenInput("");
						}}
					>
						<Icon path={mdiLink} size={1} />
					</Button>
				</div>
			</h5>

			<Row className="mb-4">
				{queryDashboard.isLoading && (
					<>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiClipboardPulseOutline} loading={true} />
						</Col>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiClipboardPulseOutline} loading={true} />
						</Col>
						<Col md={4}>
							<DashboardCard title="&nbsp;" footer="&nbsp;" icon={mdiClipboardPulseOutline} loading={true} />
						</Col>
					</>
				)}

				{!queryDashboard.isLoading && (
					<>
						{queryDashboard.data?.Result !== 1 && (
							<Col md={12}>
								<Alert variant="secondary" className="text-center">
									{queryDashboard.data?.Message || "Problema na operação. Tente novamente mais tarde"}
								</Alert>
							</Col>
						)}

						{queryDashboard.data?.Result === 1 && (
							<>
								{!queryDashboard.data?.Data?.length ? (
									<Alert variant="light" className="text-center p-4 d-flex justify-content-center align-items-center">
										<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
										Nenhum registro encontrado
									</Alert>
								) : (
									ordenarPorSaude(queryDashboard.data.Data).map((item, index) => {
										const iconClass = getSaudeIcon(item.saude);
										return (
											<Col md={4} className="mb-3" key={index}>
												<DashboardCard
													icon={mdiClipboardPulseOutline}
													iconClass={iconClass}
													title={item.nome || "Sem nome"}
													value={`${item.monitoresGoods || "0"}`}
													valueDescription={`\u00A0/ ${item.monitores || "0"}`}
													footer={formatarTempoChecagem(item.dataUltimaChecagem || "", item.ultimaLatencia || "")}
													onClick={() => {
														navigate(`/dashboardGrupo/${item.id}`);
													}}
												/>
											</Col>
										);
									})
								)}
							</>
						)}
					</>
				)}
			</Row>

			<Modal
				show={linkModal}
				onHide={() => {
					setLinkModal(false);
					setTokenInput("");
				}}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Gerar Link de Acesso</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group className="mb-3 text-center" controlId="token">
						<Form.Label>Token de Acesso</Form.Label>
						<Form.Control
							type="text"
							placeholder="Cole aqui o token de acesso do usuário"
							value={tokenInput}
							onChange={(e) => setTokenInput(e.target.value)}
							autoFocus
							className="font-monospace"
						/>
						<Form.Text className="text-muted">Informe o token gerado para o usuário que terá acesso ao monitoramento</Form.Text>
					</Form.Group>
					<Alert variant="light" className="text-center">
						<small>
							<strong>Instruções:</strong> O link gerado permitirá acesso público ao dashboard de monitoramento usando o token informado.
						</small>
					</Alert>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => {
							setLinkModal(false);
							setTokenInput("");
						}}
					>
						Cancelar
					</Button>
					<Button variant="primary" onClick={handleGerarLink} disabled={!tokenInput.trim()}>
						Copiar Link
					</Button>
					<Button variant="primary" onClick={handleGerarLinkEmbed} disabled={!tokenInput.trim()}>
						Copiar Link Embed
					</Button>
				</Modal.Footer>
			</Modal>
		</Layout>
	);
}
